import clsx from 'clsx';
import Icon from '@/app/ui/icons/Icon';
import Image from 'next/image';
import coin from '@/app/ui/sections/coin-token/images/coin.webp';
import Button from '@/app/ui/nav/Button';
import availableCrypto from '@/app/db/available_crypto';
import { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import useWindowSize from '@/app/hooks/useWindowSize';
import { useWeb3Modal } from '@web3modal/scaffold-react';
import { useAccount, useBalance } from 'wagmi';
import useWidget, { fetcher } from '@/app/hooks/useWidget';
import AvailableCryptoType from '@/app/types/components/AvailableCryptoType';
import useSwr from 'swr';

type ValuesType = { pay?: number; get?: number };

const MIN = 0;
const TEST_STATUS = false;

function Title({ text }: { text: ReactNode }) {
  return (
    <div className="mb-[21px] mt-[22px] flex w-full items-center text-center text-pale-mist">
      <div className="h-[1px] w-[88px] bg-[#9AC1E4] opacity-20" />
      <div className="flex w-full justify-center">{text}</div>
      <div className="h-[1px] w-[88px] bg-[#9AC1E4] opacity-20" />
    </div>
  );
}

export default function BuyForm() {
  const [selected, setSelected] = useState<AvailableCryptoType>({
    name: 'eth',
    chainId: 1,
  });
  const [values, setValues] = useState<ValuesType>({
    pay: 0,
    get: 0,
  });
  const { data: quote } = useSwr(
    `/api/quote?currency=${selected.name}`,
    fetcher,
  );
  const router = useRouter();
  const { isMobile } = useWindowSize();
  const [isSuccess, setIsSuccess] = useState<boolean | undefined>(undefined);
  const [isEnoughCrypto, setIsEnoughCrypto] = useState<boolean | undefined>(
    undefined,
  );
  const { open } = useWeb3Modal();
  const { address, isConnected, isConnecting } = useAccount();
  const balance = useBalance({
    address,
    token: selected.token,
    chainId: selected.chainId,
  });
  const [status, setStatus] = useState(false);
  const { submitTransaction, data } = useWidget();

  useEffect(() => {
    if (isConnecting) setStatus(true);

    if (isMobile && status && isConnected) {
      router.push('/purchase');
      setStatus(false);
    }
  }, [isConnected, isConnecting, isMobile, router, status]);

  const customOnChange = (value: string, name: 'get' | 'pay') => {
    const regex = /^\d+\.?\d*$/;
    const COST = quote.pricePerUnit;

    setValues((prev: ValuesType) => {
      if (COST && (value === '' || regex.test(value))) {
        const parsedInput = parseFloat(value);

        if (parsedInput < MIN) {
          return { ...prev, [name]: MIN.toString() };
        }

        if (parsedInput > Number(balance.data?.formatted) && name === 'pay') {
          return {
            get: Number(balance.data?.formatted) / COST,
            pay: Number(balance.data?.formatted),
          };
        }
        if (
          Number(value) * COST > Number(balance.data?.formatted) &&
          name === 'get'
        ) {
          return {
            pay: Number(balance.data?.formatted),
            get: Number(balance.data?.formatted) / COST,
          };
        }

        let calc;

        if (name === 'pay') {
          calc = Number(value) / COST;
        } else {
          calc = Number(value) * COST;
        }

        const converted = name === 'get' ? 'pay' : 'get';
        const decLen = value.split('.')[1]?.length ?? 0;
        const precisionOverflow = decLen - (balance.data?.decimals ?? 0);

        if (precisionOverflow > 0) {
          return {
            [converted]: calc,
            [name]: value.slice(0, value.length - precisionOverflow),
          };
        }

        return { [converted]: calc, [name]: value };
      }

      return { ...prev, [name]: '' };
    });
  };

  const handleSubmit = () => {
    if (Number(values.pay) > (balance.data?.value ?? 0)) {
      setIsSuccess(false);
      setIsEnoughCrypto(false);
    } else {
      setIsSuccess(TEST_STATUS);
      setIsEnoughCrypto(true);
    }
  };
  if (isSuccess === true && isEnoughCrypto === true) {
    return (
      <div className="w-full">
        <Title text={<p className="uppercase">success</p>} />
        <div className="flex flex-col items-center justify-center">
          <div className="mb-[29px] size-[92px] rounded-full bg-[#111A50] text-[#9AC1E5]">
            <Icon name="success" width={92} height={92} />
          </div>
          <p className="mb-[9px] font-bold text-[#90B6EC]">
            Your transaction is completed!
          </p>
          <p className="mb-[34px] text-baby-blue">{values.get} $GOIN</p>
          <Button
            type="button"
            onClick={() => {
              setValues({ pay: 0, get: 0 });
              setIsEnoughCrypto(undefined);
              setIsSuccess(undefined);
            }}
            className="relative w-full overflow-hidden rounded-[34px] bg-baby-blue py-3 uppercase after:absolute after:-bottom-[380px] after:left-2/4 after:h-[380px] after:w-[380px] after:-translate-x-2/4 after:rounded-full after:bg-vivid-orange after:duration-700 hover:text-ice-blue hover:after:bottom-[-150px] active:scale-95"
          >
            <span className="relative z-10">Buy more</span>
          </Button>
        </div>
      </div>
    );
  }
  if (isSuccess === false && isEnoughCrypto === true) {
    return (
      <div className="w-full">
        <Title text={<p className="uppercase">Transfer failed</p>} />
        <div className="flex flex-col items-center justify-center">
          <div className="mb-[29px] flex size-[92px] items-center justify-center rounded-full bg-[#111A50] text-[#9AC1E5]">
            <Icon name="error" width={53} height={53} />
          </div>
          <p className="mb-[38px] text-baby-blue">
            Something went wrong, please try again later.
          </p>
          <div className="flex gap-[24px]">
            <Button
              type="button"
              onClick={() => {
                setValues({ pay: 0, get: 0 });
                setIsEnoughCrypto(undefined);
                setIsSuccess(undefined);
              }}
              className="relative w-[173px] overflow-hidden rounded-[34px] border border-baby-blue bg-transparent py-3 uppercase text-baby-blue active:scale-95"
            >
              <span className="relative z-10">Go back</span>
            </Button>
            <Button
              onClick={() => handleSubmit()}
              type="button"
              className="relative w-[173px] overflow-hidden rounded-[34px] bg-baby-blue py-3 uppercase after:absolute after:-bottom-[380px] after:left-2/4 after:h-[380px] after:w-[380px] after:-translate-x-2/4 after:rounded-full after:bg-vivid-orange after:duration-700 hover:text-ice-blue hover:after:bottom-[-150px] active:scale-95"
            >
              <span className="relative z-10">Try again</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className={`${isConnected ? 'mb-0' : 'mb-[21px]'} flex gap-[9.5px]`}>
        {availableCrypto.map((crypto) => (
          <Button
            key={crypto.name}
            className={clsx(
              'flex h-[53px] w-[117px] items-center justify-center gap-4 rounded-[59px] bg-[#111A50] text-[18px] uppercase text-baby-blue',
              {
                'bg-baby-blue text-black': selected.name === crypto.name,
                'bg-[rgba(170,212,249,0.10)] text-baby-blue hover:bg-[#182570]':
                  selected.name !== crypto.name,
              },
            )}
            onClick={() => {
              setValues({ pay: 0, get: 0 });
              setSelected(crypto);
            }}
          >
            {crypto.name}
            <Icon name={crypto.name} width={27} height={27} />
          </Button>
        ))}
      </div>
      {isConnected && (
        <Title
          text={`${selected.name.toUpperCase()} Balance ${Number(balance.data?.formatted ?? 0).toFixed(8)}`}
        />
      )}
      <div className="flex w-full flex-col">
        <div
          className={clsx('flex w-full justify-between gap-4', {
            'mb-[26px]': address === null,
            'mb-[17px]': address !== null,
          })}
        >
          <div
            className={`flex flex-col ${isConnected ? 'gap-[7px]' : 'gap-4'}`}
          >
            <p className="uppercase text-[rgba(170,212,249,0.80)]">
              {selected.name}
              {` `}
              <span className="quiroh-font lowercase">you pay</span>
            </p>
            <div className="relative">
              <input
                type="text"
                value={values.pay}
                onChange={(e) => customOnChange(e.target.value, 'pay')}
                className="quiroh-font w-full rounded-[37px] border border-baby-blue bg-[rgba(2,8,39,0.45)] py-[10px] pl-[23px] pr-11 text-[18px] text-pale-mist"
              />
              <div className="absolute right-2.5 top-2.5">
                <Icon name={selected.name} width={27} height={27} />
              </div>
            </div>
          </div>
          <div
            className={`flex flex-col ${isConnected ? 'gap-[7px]' : 'gap-4'}`}
          >
            <p className="uppercase text-[rgba(170,212,249,0.80)]">
              $GOIN
              {` `}
              <span className="quiroh-font lowercase">you receive</span>
            </p>
            <div className="relative">
              <input
                type="text"
                value={values.get}
                onChange={(e) => customOnChange(e.target.value, 'get')}
                className="quiroh-font w-full rounded-[37px] border border-baby-blue bg-[rgba(2,8,39,0.45)] py-[10px] pl-[23px] pr-11 text-[18px] text-pale-mist"
              />
              <div className="absolute right-2.5 top-2.5">
                <Image
                  src={coin}
                  width={27}
                  height={27}
                  alt="$GOIN"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              </div>
            </div>
          </div>
        </div>
        {isSuccess === false && isEnoughCrypto === false && (
          <>
            <p className="quiroh-font text-center text-[14px] text-baby-blue">
              You do not have enough ETH to pay for this amount
            </p>
            <div className="mx-auto my-[15px] h-[1px] w-[88px] bg-[#9AC1E4]" />
          </>
        )}
        {isConnected && (
          <p className="quiroh-font mb-2 text-center text-xs text-[rgba(170,212,249,0.6)]">
            Make sure you have 0.015 ETH for gas and USDT fot the token
            exchange.
          </p>
        )}
        <div
          className={clsx('mt-0', {
            'lg:mt-1': isConnected,
            'lg:mt-[26px]': !isConnected,
          })}
        >
          {data?.pricePerUnit && data?.paymentAddress ? (
            <Button
              onClick={() => {
                if (!isConnected) {
                  open();
                } else if (isMobile) {
                  router.push('/purchase');
                } else {
                  submitTransaction(
                    values.pay as number,
                    data.paymentAddress,
                    selected.token ??
                      '0xdac17f958d2ee523a2206206994597c13d831ec7',
                    selected.name,
                  );
                }
              }}
              className={clsx(
                'relative flex h-[44px] w-full items-center justify-center overflow-hidden rounded-[34px] bg-vivid-orange uppercase text-ice-blue after:absolute after:-bottom-[380px] after:left-2/4 after:h-[380px] after:w-[380px] after:-translate-x-2/4 after:rounded-full after:bg-[#FF8800] after:duration-700 hover:text-ice-blue hover:after:bottom-[-150px]',
                {
                  'active:scale-95':
                    isConnected && values.pay !== 0 && values.get !== 0,
                },
              )}
              disabled={isConnected && values.pay === 0 && values.get === 0}
            >
              <span className="relative z-10">
                {!isConnected ? 'connect wallet' : 'buy tokens'}
              </span>
            </Button>
          ) : null}
        </div>
      </div>
    </>
  );
}
